import {
  Button,
  ButtonProps,
  Input as AriaInput,
  InputProps as AriaInputProps,
  GroupProps as AriaGroupProps,
  Group,
} from "react-aria-components";
import { tv, VariantProps } from "tailwind-variants";
import { forwardRef } from "react";

const inputStyle = tv({
  base: [
    "flex bg-transparent transition-[background] rounded-full border font-medium border-grey-300 text-grey-700 placeholder:text-grey-400 min-w-0 outline-offset-0",
    "data-[disabled]:text-grey-400 data-[disabled]:text-grey-400 data-[disabled]:bg-grey-200 data-[disabled]:pointer-events-none",
    "data-[focused]:outline data-[focused]:outline-3 data-[focused]:outline-purple-100 data-[focused]:text-grey-900 data-[focused]:bg-white data-[focused]:border-purple-400 data-[focused]:border-purple-400",
    "data-[hovered]:bg-grey-100 data-[hovered]:border-grey-100",
    "data-[hovered]:data-[focused]:border-purple-400 data-[hovered]:data-[focused]:bg-white",
  ],
  variants: {
    size: {
      lg: "h-12 px-6 text-base",
      md: "h-11 px-5 text-sm",
      sm: "h-8 px-4 text-xs",
    },
  },
});
interface InputProps
  extends VariantProps<typeof inputStyle>,
    Omit<AriaInputProps, "size"> {}
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ size, className, ...rest }, ref) => {
    return (
      <AriaInput
        ref={ref}
        className={
          typeof className === "function"
            ? (renderProps) =>
                inputStyle({
                  size,
                  className: className(renderProps),
                })
            : inputStyle({
                size,
                className,
              })
        }
        {...rest}
      />
    );
  },
);

const inputButtonStyle = tv({
  base: [
    "bg-grey-50 transition-[background] flex items-center justify-center rounded-full absolute first:left-0.5 last:right-0.5  ",
    "[&>svg]:group-has-[input[data-disabled]]:fill-grey-400 group-has-[input[data-disabled]]:bg-grey-200 data-[disabled]:bg-grey-200 [&>svg]:data-[disabled]:fill-grey-400",
    "data-[hovered]:bg-grey-100",
  ],
  variants: {
    size: {
      sm: "size-7 [&_svg]:size-3",
      md: "size-10 [&_svg]:size-3.5",
      lg: "size-11 [&_svg]:size-4",
    },
  },
});
interface InputButtonProps
  extends VariantProps<typeof inputStyle>,
    ButtonProps {}
export function InputButton({
  children,
  size,
  className,
  ...rest
}: InputButtonProps) {
  return (
    <Button
      className={
        typeof className === "function"
          ? (renderProps) =>
              inputButtonStyle({ size, className: className(renderProps) })
          : inputButtonStyle({ size, className })
      }
      {...rest}
    >
      {children}
    </Button>
  );
}

const inputGroupStyle = tv({
  base: [
    "group relative flex items-center [&>svg]:absolute [&>svg]:fill-grey-700 [&>svg]:pointer-events-none",
    "[&>svg]:has-[:placeholder-shown]:fill-grey-500",
    "[&>svg]:has-[input[data-disabled]]:fill-grey-400",
    "[&>svg]:has-[input[data-focused]]:fill-grey-900",
  ],
  variants: {
    size: {
      lg: "[&>svg]:size-4 [&>svg]:left-3.5",
      md: "[&>svg]:size-3.5 [&>svg]:left-3",
      sm: "[&>svg]:size-3 [&>svg]:left-2.5",
    },
  },
});
interface InputGroupProps
  extends VariantProps<typeof inputGroupStyle>,
    Omit<AriaGroupProps, "size"> {}
export function InputGroup({ children, size }: InputGroupProps) {
  return <Group className={inputGroupStyle({ size })}>{children}</Group>;
}
